import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../../supabase.types";
import { bucketReferrer } from "../comedian-weekly-digest/referrer-buckets";
import { isExcludedReferrer } from "../comedian-weekly-digest/exclusions";

const PAGE_SIZE = 10000;

export interface AudienceSourceCount {
  source: string; // referral bucket label, e.g. "Instagram"
  count: number; // unique ticket-buyer distinct_ids from that source
}

/**
 * Aggregates a single comedian's ticket-buyer events over an inclusive
 * [startTs, endTs] window into per-referral-source unique-buyer counts, reusing
 * the weekly-digest referrer bucketing. Sorted by count desc.
 */
export async function fetchAudienceBySource(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  startTs: string,
  endTs: string
): Promise<AudienceSourceCount[]> {
  const bySource = new Map<string, Set<string>>();
  let offset = 0;

  while (true) {
    // `$`-prefixed Mixpanel columns break PostgREST's typed select parser, so
    // we select("*") (matching the weekly-digest fetcher) and read fields off
    // the row.
    const { data, error } = await supabase
      .from("events_tickets_merged")
      .select("*")
      .eq("ComedianId", comedianId)
      .gte("timestamp", startTs)
      .lt("timestamp", endTs)
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) {
      throw new Error(
        `[chart-analytics] Failed to fetch audience-by-source: ${error.message}`
      );
    }

    if (!data || data.length === 0) break;

    for (const row of data) {
      if (!row.distinct_id) continue;
      if (
        isExcludedReferrer(
          row["$initial_referring_domain"] as string | null | undefined
        )
      )
        continue;

      const bucket = bucketReferrer(
        row["$initial_referring_domain"],
        row["$initial_referrer"]
      );

      let userSet = bySource.get(bucket);
      if (!userSet) {
        userSet = new Set();
        bySource.set(bucket, userSet);
      }
      userSet.add(row.distinct_id);
    }

    offset += data.length;
    if (data.length < PAGE_SIZE) break;
  }

  return Array.from(bySource.entries())
    .map(([source, users]) => ({ source, count: users.size }))
    .sort((a, b) => b.count - a.count);
}
